import type { Strings } from "@frontend/strings/strings.types";

const titleCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const en: Strings = {
	login: {
		title: "Welcome back",
		subtitle: "Sign in with the username your admin gave you.",
		heroTitle: "See it on. Before you buy it.",
		heroBody: (appName) =>
			`${appName} turns one photo of the customer and one of the garment into their new look in under a minute.`,
		username: "Username",
		password: "Password",
		submit: "Sign in",
		continueWithGoogle: "Continue with Google",
		connecting: "Connecting…",
		terms: "By continuing, you agree to our Terms of Service and Privacy Policy",
		errorOauthFailed: "Authentication failed. Please try again.",
		errorStateMismatch: "Session expired. Please try signing in again.",
		errorCallback: "There was an error during sign-in. Please try again.",
		errorGeneric: (message) => `An error occurred: ${message}`,
		failed: "Wrong username or password",
		deactivated: "This login has been switched off. Please contact your admin.",
		tooManyAttempts: "Too many attempts. Wait a few seconds and try again.",
		serverUnreachable: "Can't reach the server right now. Check the connection and try again.",
		signingIn: "Signing in…",
	},
	nav: {
		customers: "Customers",
		dashboard: "Dashboard",
		users: "Users",
		settings: "Settings",
		profile: "Profile",
		signOut: "Sign out",
	},
	roles: {
		admin: "Admin",
		member: "Member",
		adminHelp: "sees everyone's data, manages users and settings",
		memberHelp: "sees only their own customers and looks",
	},
	studio: {
		phone: "Phone number",
		phoneHelp: "Needed for WhatsApp; can be added later",
		name: "Name",
		walkInCustomer: "Walk-in customer",
		looksCount: (n) => plural(n, "look", "looks"),
		newCustomer: "New customer",
		editCustomer: "Edit customer",
		phoneTaken: "This number already belongs to another customer.",
		useExisting: "Use that customer",
		garmentType: "Garment type",
		garmentTypeRememberFailed: "Couldn't remember this garment type for your login",
		quality: "Quality",
		standard: "Standard",
		premium: "Premium",
		noImagesLeft: (tier) => `No ${tier} images left — ask your admin to top up`,
		customerPhoto: "Customer photo",
		customerPhotoHint: "Facing the camera, good light, head to waist",
		garmentPhoto: (noun) => `${titleCase(noun)} photo`,
		garmentPhotoHint: "Lay it flat or drape it so the whole design shows",
		takePhoto: "Take photo",
		stepOf: (n, total) => `Step ${n} of ${total}`,
		gallery: "Gallery",
		noCreditsTitle: "No images left",
		noCreditsHelp: "Your Standard and Premium packs are used up. Ask your admin to top up before starting a try-on.",
		uploadFromGallery: "Or upload from gallery",
		flip: "Flip",
		switchCamera: "Switch camera",
		closeCamera: "Back to the look",
		cameraUnavailable: "No camera here. Take the photo with the camera app or pick one from the gallery.",
		cameraDenied: "Camera access is blocked. Allow it in the browser settings, or pick a photo.",
		saving: "Saving…",
		backToCustomers: "Back to customers",
		addDetails: "Add phone & name",
		fromGallery: "From gallery",
		photoError: "Couldn't use this photo. Try a JPEG or PNG, or take it again.",
		uploading: "Uploading…",
		uploadFailedRetry: "Failed — tap to retry",
		changeCustomerPhoto: "Change customer photo",
		addBothPhotos: (noun) => `Add the customer and ${noun} photos`,
		addCustomerPhoto: "Now add the customer's photo",
		addGarmentPhoto: (noun) => `Now add the ${noun} photo`,
		waitingForUpload: "Finishing the upload…",
		autoStartHelp: "The look starts by itself as soon as both photos are in.",
		outOfImages: "No images left for this quality. Switch quality or ask your admin to top up.",
		startFailed: "Couldn't start the look.",
		starting: "Starting…",
		creating: "Creating the look…",
		timeLeft: (seconds) => `About ${seconds}s left`,
		almostThere: "Almost there…",
		lookFailed: "This look couldn't be created.",
		tryAgain: "Try again",
		lookN: (n) => `Look ${n}`,
		allLooks: (n) => `Looks · ${n}`,
		newLook: "New look",
		tryAnother: (noun) => `Try another ${noun}`,
		makePremium: "Make it Premium",
		regenerate: "Regenerate",
		regenerateConfirm: "This creates the look again and uses one more image from your pack.",
		whatToChange: "What should change? (optional)",
		whatToChangeExample: "e.g. brighter light, pallu over the left shoulder",
		useOneImage: "Use 1 image",
		shareImage: "Share image",
		shareFailed: (message) => `Couldn't share: ${message}`,
		savedToShare: (n) =>
			n === 1 ? "Image saved — attach it in WhatsApp." : `${n} images saved — attach them in WhatsApp.`,
		select: "Select",
		pickedCount: (n) => (n === 0 ? "Pick looks to share" : `${n} selected`),
		shareN: (n) => (n <= 1 ? "Share" : `Share ${n} images`),
	},
	customers: {
		search: "Search by number or name",
		none: "No customers yet",
		noMatch: "No customers match",
		noneHelp: "Start a try-on in the Studio and the customer appears here.",
		more: "More",
		delete: "Delete customer data",
		deleteConfirm:
			"This permanently removes the customer, their photos and every look. It can't be undone.",
		deleteAction: "Delete",
		deleted: "Customer data deleted",
	},
	dashboard: {
		title: "Dashboard",
		subtitleAdmin: "How the whole store is doing.",
		subtitleMember: "Your try-ons at a glance.",
		period: "Period",
		today: "Today",
		days: (n) => `${n} days`,
		export: "Export",
		exportLooks: "Looks (CSV)",
		exportSummary: "Summary (CSV)",
		exportTruncated: "Only the latest 10,000 looks were exported. Narrow the period for the rest.",
		person: "Person",
		everyone: "Everyone",
		looks: "Looks",
		looksN: (n) => plural(n, "look", "looks"),
		shared: "Shared",
		ofReady: (pct) => `${pct}% of finished looks`,
		customers: "Customers",
		successRate: "Success rate",
		failedN: (n) => `${n} failed`,
		looksPerDay: "Looks per day",
		topPeople: "Top people",
		noLooks: "No looks in this period",
		credits: "Images left",
		creditsUnavailable: "Couldn't load image balances right now.",
		imagesLeftLabel: (tier) => `${tier} images left`,
		packUsage: (usedPct, expires) => `${usedPct}% used · expires ${expires}`,
		noPacks: "No active pack",
		topUpContact: "To add images, contact your provider (Tezi Communications).",
		recentLooks: "Recent looks",
		quality: "Quality",
		status: "Status",
		allQualities: "All qualities",
		allStatuses: "All statuses",
		statusLabel: { queued: "Queued", processing: "Creating", ready: "Ready", failed: "Failed" },
		loadMore: "Load more",
	},
	admin: {
		products: "Products",
		productsHelp: "The product types the studio offers. The brand (name, logo, icon, colours) is set by your provider.",
		instructions: "Extra instructions",
		instructionsHelp: "Added to every look on top of the built-in prompt, e.g. \"Keep the pallu over the left shoulder\". Leave empty for none.",
		usersTitle: "Users",
		usersSubtitle: "Admins see everything. Members see only their own customers and looks.",
		newUser: "New user",
		you: "you",
		actions: "Actions",
		saved: "Saved",
		done: "Done",
		create: "Create",
		actionFailed: (message) => `Could not save: ${message}`,
		name: "Name",
		username: "Username",
		usernameHelp: "Lowercase letters, numbers, _ and . only",
		password: "Password",
		generate: "Generate",
		showPassword: "Show password",
		hidePassword: "Hide password",
		role: "Role",
		language: "Language",
		active: "Active",
		switchedOff: "Switched off",
		looks30d: "Looks (30 days)",
		lastLook: "Last look",
		resetPassword: "Reset password",
		makeAdmin: "Make admin",
		makeMember: "Make member",
		switchOff: "Switch off",
		switchOn: "Switch on",
		changeLanguageTo: (language) => `Language: ${language}`,
		shareCredentials: "Share these login details",
		shareCredentialsHelp: "The password is shown only once. Copy it now.",
		copy: "Copy",
		copied: "Copied",
		credentialsText: (appName, username, password) =>
			`${appName} login
Username: ${username}
Password: ${password}`,
		settingsTitle: "Settings",
		settingsSubtitle: "Your brand, watermark and try-on options.",
		unsavedChanges: "Unsaved changes",
		discard: "Discard",
		logoUrl: "Logo URL",
		garmentNouns: "Garment types",
		garmentNounsHelp:
			"In English, e.g. saree. The first is the default. With more than one, the studio shows a picker; each login keeps the type they picked last, for every customer they serve.",
		garmentNounPlaceholder: "e.g. lehenga",
		addGarmentNoun: "Add",
		makeDefault: "Make default",
		defaultGarment: "Default",
		removeGarmentNoun: "Remove",
		watermark: "Watermark",
		watermarkHelp: "Your logo on every look, so shared images carry your brand.",
		watermarkEnabled: "Add the logo to every look",
		watermarkLogoHelp: "Leave empty to use the brand logo",
		position: "Position",
		topLeft: "Top left",
		topRight: "Top right",
		bottomLeft: "Bottom left",
		bottomRight: "Bottom right",
		scale: "Size",
		opacity: "Opacity",
		margin: "Margin (px)",
		preview: "Preview",
		tryonOptions: "Try-on",
		tryonOptionsHelp: "Image shape and extra instructions for every look.",
		aspect: "Image shape",
	},
	offline: {
		title: "You're offline",
		body: "Photos can't upload and looks can't be created until the internet is back.",
		retry: "Retry",
		checking: "Checking…",
		backOnline: "Back online",
		studioHint: "You're offline — reconnect to add photos and create looks.",
	},
	profile: {
		title: "Profile",
		appearance: "Appearance",
		appearanceHelp: "Light, dark, or follow this device.",
		light: "Light",
		dark: "Dark",
		system: "System",
		language: "Language",
		languageSaved: "Language updated",
		camera: "Camera",
		cameraHelp: "Which camera the studio opens first. You can still flip it for any photo.",
		cameraBack: "Back",
		cameraFront: "Front",
		cameraSaved: "Camera updated",
		cameraSaveFailed: "Couldn't save the camera setting",
	},
	pwa: {
		installTitle: (appName) => `Install ${appName}`,
		installBody: (appName) => `Add ${appName} to your home screen for the best experience.`,
	},
	common: {
		language: "Language",
		english: "English",
		tamil: "தமிழ்",
		loading: "Loading…",
		retry: "Retry",
		refresh: "Refresh",
		close: "Close",
		cancel: "Cancel",
		save: "Save",
	},
};
